import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { CAL_MONTHS, CAL_DOW, buildCalGrid, calTodayStr, formatMobileDate, formatTagLabel, highlightParts, useLongPress, parseMatchScore } from '../utils/themeUtils';

// ─── Design tokens (CVFlow brand) ────────────────────────────────────────────
const CV = {
  amber:      '#FFBC00',
  amberDeep:  '#CC9000',
  amberSoft:  'rgba(255,188,0,0.12)',
  amberGlow:  'rgba(255,188,0,0.25)',
  teal:       '#004D40',
  tealSoft:   'rgba(0,77,64,0.08)',
  tealMid:    'rgba(0,77,64,0.15)',
  bgBase:     '#F5F2E9',
  bgSurface:  '#FEFCF6',
  bgElevated: '#FFFFFF',
  text:       '#1A1612',
  textMuted:  'rgba(26,22,18,0.58)',
  textDim:    'rgba(26,22,18,0.35)',
  border:     'rgba(26,22,18,0.09)',
  borderMid:  'rgba(26,22,18,0.14)',
  shadow:     '0 4px 24px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)',
  shadowCard: '0 2px 12px rgba(0,0,0,0.07), 0 1px 3px rgba(0,0,0,0.04)',
  serif:      "'DM Serif Display', 'Playfair Display', Georgia, serif",
  sans:       "'Inter', -apple-system, system-ui, sans-serif",
  radius:     {
    pill: 9999,
    card: 16,
    input: 12,
    badge: 999,
  },
};

// ─── Status badge config ──────────────────────────────────────────────────────
const STATUS_CFG = {
  abgeschickt:            { label: 'Abgeschickt', color: '#0369a1', bg: 'rgba(3,105,161,0.11)' },
  absage:                 { label: 'Absage',       color: '#b91c1c', bg: 'rgba(185,28,28,0.10)' },
  kennenlerngespraech:    { label: 'Interview',    color: '#b45309', bg: 'rgba(180,83,9,0.11)'  },
  zusage:                 { label: 'Zusage',       color: '#047857', bg: 'rgba(4,120,87,0.11)'  },
  unterlagen_angefertigt: { label: 'Unterlagen',   color: '#6b5f54', bg: 'rgba(107,95,84,0.11)' },
};

// ─── Category cards data ──────────────────────────────────────────────────────
const KATEGORIEN = [
  { label: 'Software',       sub: 'Technologie',  icon: '💻', mod: 'teal'    },
  { label: 'Design',         sub: 'Kreativ',      icon: '🎨', mod: 'amber'   },
  { label: 'Finance & Tech', sub: 'Finanzen',     icon: '📊', mod: 'surface' },
  { label: 'Data & AI',      sub: 'Data Science', icon: '🔬', mod: 'light'   },
];

// ─── Component ────────────────────────────────────────────────────────────────

// ─── Context menu style helper ────────────────────────────────────────────────
const CTX_ITEM = (danger = false) => ({
  display: 'flex', alignItems: 'center', gap: 10,
  width: '100%', padding: '12px 14px', borderRadius: 10,
  background: 'none', border: 'none', cursor: 'pointer',
  fontSize: 14, fontWeight: 500, textAlign: 'left',
  fontFamily: CV.sans,
  color: danger ? '#c0392b' : CV.text,
  WebkitTapHighlightColor: 'transparent',
});

function MobileSucheOverlay({ stellen, onSchliessen, onStelleAuswaehlen, markierteIds = new Set(), onMarkierenToggle, onDelete, onRename }) {
  const [query, setQuery]                 = useState('');
  const [kalenderOffen, setKalenderOffen] = useState(false);
  const [calDate, setCalDate]             = useState(() => new Date());
  const [selectedDay, setSelectedDay]     = useState(null);
  const [mounted, setMounted]             = useState(false);
  const [ctxMenu, setCtxMenu]             = useState(null); // { stelle, bottom, right }
  const [renamingStelle, setRenamingStelle] = useState(null);
  const [renameWert, setRenameWert]       = useState('');

  const inputRef   = useRef(null);
  const kalRef     = useRef(null);
  const kalBtnRef  = useRef(null);
  const renameRef  = useRef(null);

  useEffect(() => {
    requestAnimationFrame(() => setMounted(true));
    setTimeout(() => inputRef.current?.focus(), 260);
  }, []);

  useEffect(() => {
    if (renamingStelle) {
      setRenameWert(renamingStelle.name || '');
      setTimeout(() => { renameRef.current?.focus(); renameRef.current?.select(); }, 50);
    }
  }, [renamingStelle]);

  const openCtx = (stelle, rect) => {
    setCtxMenu({
      stelle,
      bottom: window.innerHeight - rect.top + 10,
      right: Math.max(window.innerWidth - rect.right, 10),
    });
  };
  const closeCtx = () => setCtxMenu(null);

  const handleRenameSubmit = () => {
    const trimmed = renameWert.trim();
    if (trimmed && renamingStelle && onRename) onRename(renamingStelle, trimmed);
    setRenamingStelle(null);
  };

  useEffect(() => {
    if (!kalenderOffen) return;
    const h = (e) => {
      if (kalRef.current && !kalRef.current.contains(e.target) &&
          kalBtnRef.current && !kalBtnRef.current.contains(e.target)) {
        setKalenderOffen(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [kalenderOffen]);

  useEffect(() => {
    if (selectedDay) {
      const d = new Date(selectedDay + 'T12:00:00');
      setCalDate(new Date(d.getFullYear(), d.getMonth(), 1));
    }
  }, [selectedDay]);

  const bewerbungsTage = useMemo(() => {
    const tage = new Set();
    stellen.forEach(s => { if (s.erstellt_am) tage.add(s.erstellt_am.slice(0, 10)); });
    return tage;
  }, [stellen]);

  const sorted = useMemo(() =>
    [...stellen].sort((a, b) => {
      const da = a.erstellt_am ? new Date(a.erstellt_am) : new Date(0);
      const db = b.erstellt_am ? new Date(b.erstellt_am) : new Date(0);
      return db - da;
    }), [stellen]);

  let filtered = sorted;
  if (query.trim()) {
    const q = query.toLowerCase();
    filtered = filtered.filter(s =>
      (s.name || '').toLowerCase().includes(q) ||
      (s.titel || '').toLowerCase().includes(q) ||
      (s.stellenbezeichnung || '').toLowerCase().includes(q) ||
      (s.empfaenger_unternehmen || '').toLowerCase().includes(q) ||
      (s.unternehmen || '').toLowerCase().includes(q)
    );
  }
  if (selectedDay) {
    filtered = filtered.filter(s => s.erstellt_am?.slice(0, 10) === selectedDay);
  }

  const year  = calDate.getFullYear();
  const month = calDate.getMonth();
  const cells = buildCalGrid(year, month);
  const today = calTodayStr();

  const cellStr = (day) =>
    `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  const isFiltering = query.trim() || selectedDay;

  return (
    <div style={{
      position: 'absolute', inset: 0,
      background: CV.bgBase,
      display: 'flex', flexDirection: 'column',
      fontFamily: CV.sans,
      WebkitFontSmoothing: 'antialiased',
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
      opacity: mounted ? 1 : 0,
      transform: mounted ? 'translateY(0)' : 'translateY(16px)',
      transition: 'opacity 0.28s cubic-bezier(0.22,1,0.36,1), transform 0.28s cubic-bezier(0.22,1,0.36,1)',
    }}>
      {/* ── Context menu portal ── */}
      {ctxMenu && ReactDOM.createPortal((() => {
        const cs = ctxMenu.stelle;
        const csMatch = parseMatchScore(cs.analyse) ?? cs.match_score ?? null;
        const csMatchColor = csMatch === null ? CV.amber
          : csMatch >= 75 ? '#22a06b'
          : csMatch >= 50 ? CV.amber
          : '#e2812d';
        const istMarkiert = markierteIds.has(cs.id);
        return (
          <>
            <div
              style={{
                position: 'fixed', inset: 0,
                backdropFilter: 'blur(7px)', WebkitBackdropFilter: 'blur(7px)',
                background: 'rgba(15,12,8,0.32)', zIndex: 1100,
              }}
              onTouchStart={closeCtx}
              onMouseDown={closeCtx}
            />
            <div
              style={{
                position: 'fixed',
                bottom: ctxMenu.bottom,
                right: ctxMenu.right,
                background: CV.bgElevated,
                border: `1px solid ${CV.border}`,
                borderRadius: 18,
                boxShadow: '0 20px 50px rgba(0,0,0,0.22), 0 4px 14px rgba(0,0,0,0.10)',
                minWidth: 230, padding: 6, zIndex: 1101,
                animation: 'dropdownIn 0.15s ease',
              }}
              onTouchStart={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {/* Stelle-Vorschau */}
              <div style={{ padding: '12px 14px 10px' }}>
                <div style={{
                  fontSize: 15, fontWeight: 700, color: CV.text,
                  letterSpacing: -0.3, lineHeight: 1.3,
                  fontFamily: CV.sans,
                  marginBottom: csMatch !== null ? 10 : 0,
                }}>
                  {cs.name || cs.titel || cs.stellenbezeichnung || 'Stelle'}
                </div>
                {cs.empfaenger_unternehmen || cs.unternehmen ? (
                  <div style={{ fontSize: 12, color: CV.textMuted, marginTop: 2, marginBottom: csMatch !== null ? 10 : 0 }}>
                    {cs.empfaenger_unternehmen || cs.unternehmen}
                  </div>
                ) : null}
                {csMatch !== null && (
                  <>
                    <div style={{ height: 4, borderRadius: 999, background: CV.border, overflow: 'hidden', marginBottom: 5 }}>
                      <div style={{ height: '100%', width: `${csMatch}%`, background: csMatchColor, borderRadius: 999 }} />
                    </div>
                    <div style={{ fontSize: 11, color: csMatchColor, fontWeight: 700 }}>
                      {csMatch}% Übereinstimmung
                    </div>
                  </>
                )}
              </div>
              <div style={{ height: 1, background: CV.border, margin: '0 6px 4px' }} />

              {/* Markieren */}
              <button
                style={CTX_ITEM(false)}
                onClick={() => { onMarkierenToggle && onMarkierenToggle(cs.id); closeCtx(); }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M9 22H15C20 22 22 20 22 15V9C22 4 20 2 15 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22Z" stroke={istMarkiert ? CV.amber : CV.text} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17 2.43945V12.4195C17 14.3895 15.59 15.1595 13.86 14.1195L12.54 13.3295C12.24 13.1495 11.76 13.1495 11.46 13.3295L10.14 14.1195C8.41 15.1495 7 14.3895 7 12.4195V2.43945" stroke={istMarkiert ? CV.amber : CV.text} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {istMarkiert ? 'Markierung entfernen' : 'Markieren'}
              </button>

              {/* Umbenennen */}
              <button
                style={CTX_ITEM(false)}
                onClick={() => { setRenamingStelle(cs); closeCtx(); }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M5.53999 19.5196C4.92999 19.5196 4.35999 19.3096 3.94999 18.9196C3.42999 18.4296 3.17999 17.6896 3.26999 16.8896L3.63999 13.6496C3.70999 13.0396 4.07999 12.2296 4.50999 11.7896L12.72 3.09956C14.77 0.929561 16.91 0.869561 19.08 2.91956C21.25 4.96956 21.31 7.10956 19.26 9.27956L11.05 17.9696C10.63 18.4196 9.84999 18.8396 9.23999 18.9396L6.01999 19.4896C5.84999 19.4996 5.69999 19.5196 5.53999 19.5196ZM15.93 2.90956C15.16 2.90956 14.49 3.38956 13.81 4.10956L5.59999 12.8096C5.39999 13.0196 5.16999 13.5196 5.12999 13.8096L4.75999 17.0496C4.71999 17.3796 4.79999 17.6496 4.97999 17.8196C5.15999 17.9896 5.42999 18.0496 5.75999 17.9996L8.97999 17.4496C9.26999 17.3996 9.74999 17.1396 9.94999 16.9296L18.16 8.23956C19.4 6.91956 19.85 5.69956 18.04 3.99956C17.24 3.22956 16.55 2.90956 15.93 2.90956Z" fill={CV.text}/>
                  <path d="M17.3399 10.9508C14.1499 10.6408 11.6399 8.27083 11.1599 5.17083C11.0999 4.76083 11.3799 4.38083 11.7899 4.31083C12.1999 4.25083 12.5799 4.53083 12.6499 4.94083C13.0299 7.36083 14.9899 9.22083 17.4299 9.46083C17.8399 9.50083 18.1399 9.87083 18.0999 10.2808C18.0499 10.6608 17.7199 10.9508 17.3399 10.9508Z" fill={CV.text}/>
                  <path d="M21 22.75H3C2.59 22.75 2.25 22.41 2.25 22C2.25 21.59 2.59 21.25 3 21.25H21C21.41 21.25 21.75 21.59 21.75 22C21.75 22.41 21.41 22.75 21 22.75Z" fill={CV.text}/>
                </svg>
                Umbenennen
              </button>

              <div style={{ height: 1, background: CV.border, margin: '4px 6px' }} />

              {/* Löschen */}
              <button
                style={CTX_ITEM(true)}
                onClick={() => { closeCtx(); onDelete && onDelete(cs); }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M21 5.98047C17.67 5.65047 14.32 5.48047 10.98 5.48047C9 5.48047 7.02 5.58047 5.04 5.78047L3 5.98047" stroke="#c0392b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8.5 4.97L8.72 3.66C8.88 2.71 9 2 10.69 2H13.31C15 2 15.13 2.75 15.28 3.67L15.5 4.97" stroke="#c0392b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M18.85 9.14062L18.2 19.2106C18.09 20.7806 18 22.0006 15.21 22.0006H8.79002C6.00002 22.0006 5.91002 20.7806 5.80002 19.2106L5.15002 9.14062" stroke="#c0392b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10.33 16.5H13.66" stroke="#c0392b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9.5 12.5H14.5" stroke="#c0392b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Löschen
              </button>
            </div>
          </>
        );
      })(), document.body)}

      {/* ── Rename modal portal ── */}
      {renamingStelle && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1200,
            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            background: 'rgba(15,12,8,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 24px',
          }}
          onTouchStart={handleRenameSubmit}
          onMouseDown={handleRenameSubmit}
        >
          <div
            style={{
              background: CV.bgElevated, borderRadius: 18, padding: '20px 18px',
              width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.22)',
            }}
            onTouchStart={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{
              fontSize: 10, fontWeight: 700, color: CV.textDim,
              marginBottom: 12, letterSpacing: '0.12em', textTransform: 'uppercase',
            }}>
              Umbenennen
            </div>
            <input
              ref={renameRef}
              value={renameWert}
              onChange={(e) => setRenameWert(e.target.value)}
              onBlur={handleRenameSubmit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRenameSubmit();
                if (e.key === 'Escape') setRenamingStelle(null);
              }}
              style={{
                width: '100%', fontSize: 18, fontWeight: 700, color: CV.text,
                letterSpacing: -0.3, background: 'transparent', border: 'none',
                borderBottom: `2px solid ${CV.amber}`, outline: 'none',
                padding: '0 0 6px', fontFamily: CV.sans,
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>,
        document.body
      )}

      {/* ── Ambient top glow ── */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0,
        height: 280, pointerEvents: 'none', zIndex: 0,
        background: 'radial-gradient(ellipse 80% 60% at 60% -10%, rgba(255,188,0,0.13) 0%, transparent 70%)',
      }} />

      {/* ── Header ── */}
      <div style={{
        position: 'relative', zIndex: 1,
        padding: '0 20px 0',
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 20px)',
        flexShrink: 0,
      }}>
        {/* Title row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6,
            }}>
              <div style={{ width: 18, height: 2, borderRadius: 2, background: CV.amber }} />
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '0.16em',
                textTransform: 'uppercase', color: CV.amber,
                fontFamily: CV.sans,
              }}>Bewerbungen</span>
            </div>
            <h1 style={{
              fontFamily: CV.serif,
              fontSize: 38, fontWeight: 400, lineHeight: 1.05,
              color: CV.teal, margin: 0, letterSpacing: '-0.5px',
            }}>
              Suchen
            </h1>
          </div>

          <button
            onClick={onSchliessen}
            style={{
              marginTop: 6,
              width: 36, height: 36, borderRadius: 999, flexShrink: 0,
              background: 'rgba(26,22,18,0.06)',
              border: `1px solid ${CV.border}`,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              WebkitTapHighlightColor: 'transparent',
              transition: 'background 0.15s',
            }}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M1 1l8 8M9 1L1 9" stroke={CV.textMuted} strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* ── Search bar ── */}
        <div style={{ position: 'relative', marginBottom: kalenderOffen ? 0 : 6 }}>
          <div style={{
            display: 'flex', alignItems: 'center',
            background: CV.bgElevated,
            borderRadius: CV.radius.pill,
            padding: '0 6px 0 18px',
            border: `1.5px solid ${kalenderOffen || selectedDay ? CV.amber : CV.border}`,
            boxShadow: kalenderOffen || selectedDay
              ? `0 0 0 3px ${CV.amberGlow}, ${CV.shadow}`
              : CV.shadow,
            transition: 'border-color 0.2s, box-shadow 0.2s',
          }}>
            <svg width="17" height="17" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, opacity: 0.4 }}>
              <circle cx="9" cy="9" r="6" stroke={CV.teal} strokeWidth="1.6"/>
              <path d="M14 14l3.5 3.5" stroke={CV.teal} strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Firma, Position, Stichwort…"
              style={{
                flex: 1, border: 'none', background: 'transparent', outline: 'none',
                fontSize: 15, color: CV.text, padding: '14px 10px',
                fontFamily: CV.sans,
              }}
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                style={{
                  background: 'rgba(26,22,18,0.06)', border: 'none', cursor: 'pointer',
                  color: CV.textMuted, fontSize: 13, padding: 0,
                  width: 26, height: 26, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, WebkitTapHighlightColor: 'transparent',
                }}
              >✕</button>
            )}

            <div style={{ width: 1, height: 20, background: CV.border, flexShrink: 0, margin: '0 6px' }} />

            {/* Calendar toggle */}
            <button
              ref={kalBtnRef}
              onClick={() => setKalenderOffen(v => !v)}
              style={{
                width: 40, height: 40, border: 'none', borderRadius: 999, cursor: 'pointer',
                background: kalenderOffen || selectedDay ? CV.amberSoft : 'transparent',
                color: kalenderOffen || selectedDay ? CV.amberDeep : CV.textDim,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, position: 'relative',
                transition: 'background 0.15s, color 0.15s',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                <path d="M8 2V5M16 2V5M3.5 9.09H20.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {selectedDay && (
                <span style={{
                  position: 'absolute', top: 7, right: 7,
                  width: 6, height: 6, borderRadius: '50%',
                  background: CV.amber, border: `1.5px solid ${CV.bgElevated}`,
                }} />
              )}
            </button>
          </div>

          {/* ── Inline Calendar ── */}
          {kalenderOffen && (
            <div ref={kalRef} style={{
              marginTop: 10,
              background: CV.bgElevated,
              border: `1.5px solid ${CV.border}`,
              borderRadius: CV.radius.card,
              padding: '14px 12px 12px',
              boxShadow: '0 16px 48px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.07)',
            }}>
              {/* Month nav */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <button
                  onClick={() => setCalDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
                  style={calNavBtn}
                >‹</button>
                <span style={{
                  fontFamily: CV.serif, fontSize: 15, fontWeight: 400,
                  color: CV.teal, letterSpacing: '-0.2px',
                }}>
                  {CAL_MONTHS[month]} {year}
                </span>
                <button
                  onClick={() => setCalDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
                  style={calNavBtn}
                >›</button>
              </div>

              {/* Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
                {CAL_DOW.map(d => (
                  <div key={d} style={{
                    textAlign: 'center', fontSize: 9, fontWeight: 700,
                    color: CV.textDim, paddingBottom: 6,
                    textTransform: 'uppercase', letterSpacing: '0.08em',
                  }}>{d}</div>
                ))}
                {cells.map((day, i) => {
                  if (day === null) return <div key={`b-${i}`} style={{ aspectRatio: '1' }} />;
                  const ds         = cellStr(day);
                  const isToday    = ds === today;
                  const hasApp     = bewerbungsTage.has(ds);
                  const isSelected = ds === selectedDay;
                  return (
                    <div
                      key={ds}
                      onClick={() => { setSelectedDay(isSelected ? null : ds); setKalenderOffen(false); }}
                      style={{
                        aspectRatio: '1',
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, borderRadius: 7, cursor: 'pointer',
                        userSelect: 'none', position: 'relative', gap: 2,
                        background: isSelected ? CV.teal : 'transparent',
                        border: isToday && !isSelected
                          ? `1.5px solid ${CV.amber}`
                          : '1.5px solid transparent',
                        color: isSelected ? '#fff' : CV.text,
                        fontWeight: isSelected || isToday ? 700 : hasApp ? 600 : 400,
                        WebkitTapHighlightColor: 'transparent',
                        transition: 'background 0.12s',
                      }}
                    >
                      <span>{day}</span>
                      {hasApp && (
                        <span style={{
                          width: 4, height: 4, borderRadius: '50%',
                          background: isSelected ? 'rgba(255,255,255,0.6)' : CV.amber,
                          flexShrink: 0,
                        }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Active day badge */}
          {selectedDay && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              marginTop: 10, padding: '5px 10px 5px 14px',
              background: CV.amberSoft,
              border: `1px solid rgba(255,188,0,0.25)`,
              borderRadius: 999, fontSize: 12, color: CV.amberDeep, fontWeight: 600,
            }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" style={{ opacity: 0.7 }}>
                <path d="M8 2V5M16 2V5M3.5 9.09H20.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>{formatTagLabel(selectedDay)}</span>
              <button
                onClick={() => { setSelectedDay(null); setKalenderOffen(false); }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: CV.amberDeep, fontSize: 16, lineHeight: 1, padding: '0 2px',
                  opacity: 0.65, WebkitTapHighlightColor: 'transparent',
                }}
              >×</button>
            </div>
          )}
        </div>
      </div>

      {/* ── Results ── */}
      <div style={{
        flex: 1, overflowY: 'auto', position: 'relative', zIndex: 1,
        padding: '16px 20px',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 32px)',
      }}>
        {/* Section header */}
        <div style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          marginBottom: 14,
        }}>
          <span style={{
            fontFamily: CV.serif, fontSize: 20, fontWeight: 400,
            color: CV.teal, letterSpacing: '-0.3px',
          }}>
            {selectedDay ? formatTagLabel(selectedDay) : query.trim() ? 'Treffer' : 'Neueste'}
          </span>
          <span style={{
            fontSize: 12, color: CV.textDim, fontWeight: 500,
            background: CV.tealSoft, borderRadius: 999,
            padding: '3px 10px', letterSpacing: '0.01em',
          }}>
            {filtered.length} {filtered.length === 1 ? 'Eintrag' : 'Einträge'}
          </span>
        </div>

        {/* Empty state */}
        {filtered.length === 0 && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: 14, padding: '56px 0',
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: 18,
              background: CV.tealSoft,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="26" height="26" viewBox="0 0 20 20" fill="none" style={{ opacity: 0.45 }}>
                <circle cx="9" cy="9" r="6" stroke={CV.teal} strokeWidth="1.5"/>
                <path d="M14 14l3.5 3.5" stroke={CV.teal} strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: CV.teal, marginBottom: 4 }}>
                Nichts gefunden
              </div>
              <div style={{ fontSize: 13, color: CV.textMuted, lineHeight: 1.5 }}>
                {selectedDay
                  ? 'Keine Bewerbungen an diesem Tag.'
                  : `Kein Treffer für „${query}"`}
              </div>
            </div>
          </div>
        )}

        {/* Result list */}
        {filtered.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {filtered.map((s, idx) => {
              const titel       = s.name || s.titel || s.stellenbezeichnung || 'Unbenannte Stelle';
              const unternehmen = s.empfaenger_unternehmen || s.unternehmen || null;
              const titelParts  = highlightParts(titel, query);
              const firmaMatch  = unternehmen
                ? highlightParts(unternehmen, query)
                : null;
              const statusCfg   = s.bewerbungs_status ? STATUS_CFG[s.bewerbungs_status] : null;

              return (
                <ResultCard
                  key={s.id}
                  delay={idx < 8 ? idx * 30 : 0}
                  onClick={() => { onSchliessen(); setTimeout(() => onStelleAuswaehlen(s), 340); }}
                  onLongPress={(rect) => openCtx(s, rect)}
                >
                  {/* Left: text */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 15, fontWeight: 600,
                      color: CV.text, lineHeight: 1.3,
                    }}>
                      {titelParts.map((p, i) =>
                        p.highlight
                          ? <mark key={i} style={{
                              background: CV.amberSoft, color: CV.amberDeep,
                              fontWeight: 700, borderRadius: 3, padding: '0 2px',
                            }}>{p.text}</mark>
                          : <span key={i}>{p.text}</span>
                      )}
                    </div>
                    {firmaMatch && (
                      <div style={{ fontSize: 12, color: CV.textMuted, marginTop: 3, lineHeight: 1.3 }}>
                        {firmaMatch.map((p, i) =>
                          p.highlight
                            ? <mark key={i} style={{
                                background: CV.amberSoft, color: CV.amberDeep,
                                fontWeight: 700, borderRadius: 3, padding: '0 2px',
                              }}>{p.text}</mark>
                            : <span key={i}>{p.text}</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Right: bookmark + badge + date */}
                  <div style={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'flex-end', gap: 5, flexShrink: 0, marginLeft: 10,
                  }}>
                    {markierteIds.has(s.id) && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M9 22H15C20 22 22 20 22 15V9C22 4 20 2 15 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22Z" fill="rgba(255,102,0,0.15)" stroke="#FF6600" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M17 2.43945V12.4195C17 14.3895 15.59 15.1595 13.86 14.1195L12.54 13.3295C12.24 13.1495 11.76 13.1495 11.46 13.3295L10.14 14.1195C8.41 15.1495 7 14.3895 7 12.4195V2.43945" fill="#FF6600" stroke="#FF6600" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                    {statusCfg && (
                      <span style={{
                        fontSize: 10, fontWeight: 700,
                        padding: '3px 9px', borderRadius: 999,
                        color: statusCfg.color, background: statusCfg.bg,
                        whiteSpace: 'nowrap', letterSpacing: '0.02em',
                      }}>
                        {statusCfg.label}
                      </span>
                    )}
                    <span style={{
                      fontSize: 11, color: CV.textDim,
                      whiteSpace: 'nowrap', letterSpacing: '0.01em',
                    }}>
                      {formatMobileDate(s.erstellt_am)}
                    </span>
                  </div>
                </ResultCard>
              );
            })}
          </div>
        )}

        {/* ── Kategorien (only when not filtering) ── */}
        {!isFiltering && (
          <div style={{ marginTop: 32 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10,
            }}>
              <div style={{ width: 14, height: 2, borderRadius: 2, background: CV.amber }} />
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '0.14em',
                textTransform: 'uppercase', color: CV.amber,
              }}>Entdecken</span>
            </div>
            <h2 style={{
              fontFamily: CV.serif, fontSize: 20, fontWeight: 400,
              color: CV.teal, margin: '0 0 14px', letterSpacing: '-0.3px',
            }}>
              Kategorien
            </h2>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10,
            }}>
              {KATEGORIEN.map(k => (
                <KategoriCard key={k.label} k={k} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Result card with subtle enter animation + long press ────────────────────
function ResultCard({ children, onClick, delay, onLongPress }) {
  const [vis, setVis] = useState(false);
  const cardRef   = useRef(null);
  const lpFiredRef = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setVis(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  const lp = useLongPress(() => {
    lpFiredRef.current = true;
    if (!cardRef.current || !onLongPress) return;
    const rect = cardRef.current.getBoundingClientRect();
    onLongPress(rect);
  });

  const handleClick = () => {
    if (lpFiredRef.current) { lpFiredRef.current = false; return; }
    onClick && onClick();
  };

  return (
    <button
      ref={cardRef}
      onClick={handleClick}
      style={{
        width: '100%', textAlign: 'left',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: CV.bgElevated,
        border: `1px solid ${CV.border}`,
        borderLeft: `3px solid ${CV.amber}`,
        borderRadius: CV.radius.card,
        padding: '14px 15px',
        cursor: 'pointer', fontFamily: CV.sans,
        boxShadow: CV.shadowCard,
        WebkitTapHighlightColor: 'transparent',
        opacity: vis ? 1 : 0,
        transform: vis ? 'translateY(0)' : 'translateY(6px)',
        transition: 'opacity 0.22s ease, transform 0.22s ease, box-shadow 0.15s, border-color 0.15s',
      }}
      onTouchStart={(e) => {
        lpFiredRef.current = false;
        lp.onTouchStart(e);
        e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)';
        e.currentTarget.style.background = '#FEFCF6';
      }}
      onTouchEnd={(e) => {
        lp.onTouchEnd(e);
        e.currentTarget.style.boxShadow = CV.shadowCard;
        e.currentTarget.style.background = CV.bgElevated;
      }}
      onTouchMove={lp.onTouchMove}
      onMouseDown={lp.onMouseDown}
      onMouseUp={lp.onMouseUp}
      onMouseLeave={lp.onMouseLeave}
    >
      {children}
    </button>
  );
}

// ─── Kategori card ────────────────────────────────────────────────────────────
function KategoriCard({ k }) {
  const isTeal  = k.mod === 'teal';
  const isAmber = k.mod === 'amber';
  return (
    <div style={{
      borderRadius: CV.radius.card,
      padding: '20px 16px',
      display: 'flex', flexDirection: 'column', gap: 5,
      boxShadow: CV.shadowCard,
      background: isTeal ? CV.teal : isAmber ? CV.amber : CV.bgElevated,
      border: !isTeal && !isAmber ? `1px solid ${CV.border}` : 'none',
      cursor: 'default',
      userSelect: 'none',
    }}>
      <span style={{ fontSize: 24, marginBottom: 2 }}>{k.icon}</span>
      <strong style={{
        fontFamily: CV.serif, fontSize: 15, fontWeight: 400,
        letterSpacing: '-0.2px', lineHeight: 1.2,
        color: isTeal ? '#fff' : isAmber ? CV.teal : CV.text,
      }}>{k.label}</strong>
      <span style={{
        fontSize: 11,
        color: isTeal ? 'rgba(255,255,255,0.55)'
          : isAmber ? 'rgba(0,77,64,0.6)'
          : CV.textMuted,
      }}>{k.sub}</span>
    </div>
  );
}

// ─── Shared styles ────────────────────────────────────────────────────────────
const calNavBtn = {
  width: 30, height: 30, borderRadius: 8,
  background: 'rgba(26,22,18,0.05)',
  border: 'none', cursor: 'pointer',
  fontSize: 19, color: CV.textMuted,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  lineHeight: 1, WebkitTapHighlightColor: 'transparent',
};

export default MobileSucheOverlay;
